import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceCTA from "../../components/ServiceCTA";
import ServiceHero from "../../components/ServiceHero";
import ContentImageSection from "../../components/sections/ContentImageSection";
import { CardColumn, HorizontalScroll } from "../../components/sections";
import { FiHeadphones, FiPhone, FiCheckCircle, FiServer, FiShield, FiZap, FiDollarSign, FiTrendingUp, FiUsers, FiClipboard, FiLifeBuoy, FiActivity, FiCreditCard, FiShoppingCart, FiCpu, FiBarChart2, FiBook, FiMapPin, FiPackage } from "react-icons/fi";
import bgImage from "../../images/img4.jpg";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const VoiceTelecomPage = () => {
  usePageMetadata({
    title: 'Voice & Telecom - Bercastle',
    description: 'AI-enhanced voice technology and BPO call center services with quality control and monitoring.',
    pageType: 'service',
    content: {
      service: {
        name: "Voice & Telecom",
        tagline: "AI-Enhanced Communication Solutions",
        description: "Next-generation voice infrastructure and BPO services powered by artificial intelligence.",
        url: "/services/voice-telecom"
      },
      capabilities: [
        {
          name: "AI-Enhanced Voice Technology",
          description: "Next-generation voice infrastructure powered by AI. Advanced speech recognition, sentiment analysis, and real-time transcription.",
          features: ["Real-time speech recognition", "Sentiment analysis", "Voice analytics", "Automated transcription", "Multi-language support"]
        },
        {
          name: "BPO Call Center Services",
          description: "Comprehensive call center solutions with skilled agents and AI support.",
          features: ["Inbound customer support", "Outbound sales campaigns", "Technical support", "Lead generation", "Appointment scheduling", "Order processing"]
        },
        {
          name: "Quality Control & Monitoring",
          description: "AI-powered quality assurance with real-time call monitoring and automated compliance checking.",
          features: ["Real-time call monitoring", "Automated quality scoring", "Compliance checking", "Performance dashboards", "Agent coaching tools"]
        },
        {
          name: "Infrastructure & Technology",
          description: "Robust telecom infrastructure with redundant systems, secure connections, and scalable architecture."
        }
      ],
      industries: ["E-commerce", "Healthcare", "Financial Services", "Technology/SaaS", "Travel & Hospitality", "Retail", "Insurance"],
      value_propositions: [
        "AI-enhanced quality and efficiency",
        "Experienced multilingual agents",
        "24/7 availability",
        "Scalable solutions",
        "Advanced analytics",
        "Secure infrastructure"
      ],
      meta: {
        source_url: "https://bercastle.com/services/voice-telecom",
        service_category: "Voice & Telecom"
      }
    }
  });
  const voiceServices = [
    {
      icon: FiHeadphones,
      title: "AI-Enhanced Voice Technology",
      description: "Next-generation voice infrastructure powered by artificial intelligence. Advanced speech recognition, sentiment analysis, and real-time transcription for improved customer experiences."
    },
    {
      icon: FiPhone,
      title: "BPO Call Center Services",
      description: "Comprehensive call center solutions with skilled agents and AI support. Inbound and outbound services, customer support, sales, technical assistance, and appointment setting."
    },
    {
      icon: FiCheckCircle,
      title: "Quality Control & Monitoring",
      description: "AI-powered quality assurance with real-time call monitoring, automated compliance checking, performance analytics, and coaching tools to ensure exceptional service standards."
    },
    {
      icon: FiServer,
      title: "Scalable Infrastructure",
      description: "Enterprise-grade telecommunications infrastructure that scales with your business. Cloud-based solutions, redundancy, disaster recovery, and 24/7 technical support."
    }
  ];

  const approachItems = [
    {
      icon: FiShield,
      title: "Reliability First",
      description: "Built on proven infrastructure with multiple redundancy layers and 99.99% uptime guarantee"
    },
    {
      icon: FiZap,
      title: "AI-Powered Insights",
      description: "Real-time analytics, sentiment analysis, and automated quality scoring for continuous improvement"
    },
    {
      icon: FiDollarSign,
      title: "Cost Effective",
      description: "Flexible pricing models and efficient operations that maximize your ROI"
    }
  ];

  const consultingServices = [
    {
      icon: FiTrendingUp,
      title: "Workflow Optimization",
      description: "Design efficient workflows and process improvements"
    },
    {
      icon: FiUsers,
      title: "Agent Training",
      description: "Performance coaching and skill development"
    },
    {
      icon: FiClipboard,
      title: "Compliance Guidance",
      description: "Regulatory compliance and best practices"
    },
    {
      icon: FiLifeBuoy,
      title: "24/7 Support",
      description: "Technical support and strategic consultation"
    }
  ];

  const industryCards = [
    { icon: FiActivity, title: "Healthcare", description: "" },
    { icon: FiCreditCard, title: "Financial Services", description: "" },
    { icon: FiShoppingCart, title: "E-commerce", description: "" },
    { icon: FiCpu, title: "Technology", description: "" },
    { icon: FiBarChart2, title: "Insurance", description: "" },
    { icon: FiBook, title: "Education", description: "" },
    { icon: FiMapPin, title: "Travel", description: "" },
    { icon: FiPackage, title: "Retail", description: "" }
  ];
  return (
    <Layout classmain="voice-telecom-page">
      <Seo
        title='Voice & Telecom Solutions - Bercastle'
        metaDesciption='Over a decade of telecommunications expertise with AI-enhanced voice solutions, call center technology, quality control systems, and scalable infrastructure.'
      />
      <Header/>

      <ServiceHero 
        badge="Voice & Telecom"
        title="Next-Generation"
        titleHighlight="Voice Solutions"
        description="Over a decade of telecommunications expertise delivering AI-enhanced voice solutions, scalable call center technology, quality control systems, and robust infrastructure for modern communication needs."
        ctaLink="/contact"
      />

      {/* About Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <ContentImageSection
            image={bgImage}
            imageAlt="Decade of Voice Expertise"
          >
            <div>
              <h2 className='text-4xl md:text-5xl mb-6'>Decade of Voice Expertise</h2>
              <div className='w-20 h-1 bg-brand-teal mb-6'></div>
              <div className='space-y-6'>
                <p className='text-xl text-slate-600 leading-relaxed'>
                  Our seasoned team boasts over a decade of experience delivering scalable and robust technology solutions within the telecommunications industry. We combine deep technical knowledge with AI innovation to transform voice communications.
                </p>
                <p className='text-xl text-slate-600 leading-relaxed'>
                  Our comprehensive offerings encompass cutting-edge technology infrastructure, BPO call-center services, quality control systems, and process assistance. Contact us to explore how we can help you tackle your next voice challenge.
                </p>
              </div>
            </div>
          </ContentImageSection>
        </div>
      </div>

      {/* Services Detail Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <CardColumn 
            cards={voiceServices} 
            cols={2}
            title="Our Voice & Telecom Services"
            description="Comprehensive telecommunications solutions enhanced by artificial intelligence"
          />
        </div>
      </div>

      {/* Technology & Process Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <CardColumn 
            cards={approachItems} 
            cols={3}
            title="Our Approach"
            description="Combining traditional telecommunications excellence with cutting-edge AI technology"
          />
        </div>
      </div>

      {/* Technology & Process Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
            <HorizontalScroll 
              items={consultingServices}
              title="Process Assistance & Consulting"
              description="Beyond technology, we provide strategic consulting and process optimization services"
            />
        </div>
      </div>

      {/* Industry Experience Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <CardColumn 
            cards={industryCards}
            cols={4}
            title="Industries We Serve"
          />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Tackle Your Voice Challenge?"
        description="Let's discuss how our decade of telecommunications expertise can transform your voice operations."
        buttonText="Get in Touch"
      />

      <Footer/>
    </Layout>
  )
}

export default VoiceTelecomPage
